import { useState, useCallback } from 'react';
// import { io } from 'socket.io-client'; // Temporarily disabled for deployment
import { debug, warn } from '../utils/logger';

const useWebSocket = (user, isLoggedIn) => {
  const [socket] = useState(null); // setSocket temporarily disabled
  const [connected] = useState(false); // setConnected temporarily disabled
  const [activeUsers] = useState(0); // setActiveUsers temporarily disabled
  const [adminMetrics] = useState(null); // setAdminMetrics temporarily disabled
  const [notifications, setNotifications] = useState([]);
  const [generationStatus] = useState({}); // setGenerationStatus temporarily disabled
  
  // const reconnectAttempts = useRef(0);
  // const maxReconnectAttempts = 5;

  // WebSocket connection temporarily disabled for deployment
  // Will be re-enabled once socket.io-client is properly configured

  // Get session ID
  const getSessionId = useCallback(() => {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }, []);

  // Track event via HTTP (fallback)
  const trackEvent = useCallback(async (eventType, eventData = {}) => {
    try {
      const response = await fetch('/api/realtime?action=track-event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          eventType,
          eventData: {
            ...eventData,
            sessionId: getSessionId(),
            page: window.location.pathname
          },
          userId: user?.id
        })
      });

      if (!response.ok) {
        warn('Failed to track event', { eventType });
      }
    } catch (err) {
      warn('Error tracking event', { error: err.message, stack: err.stack });
    }
  }, [user, getSessionId]);

  // Track user activity (fallback to HTTP)
  const trackActivity = useCallback((activityType, data = {}) => {
    // WebSocket disabled, using HTTP fallback
    trackEvent(activityType, data);
  }, [trackEvent]);

  // Track page views
  const trackPageView = useCallback((page) => {
    trackActivity('page_view', { page });
  }, [trackActivity]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track user actions
  const trackAction = useCallback((action, data = {}) => {
    trackActivity('user_action', { action, ...data });
  }, [trackActivity]);

  // Notify generation started (HTTP fallback)
  const notifyGenerationStarted = useCallback((generationData) => {
    debug('Generation started', generationData);
    // WebSocket disabled, could add HTTP notification here
  }, []);

  // Update generation progress (HTTP fallback)
  const updateGenerationProgress = useCallback((generationId, progress, status, message) => {
    debug('Generation progress', { generationId, progress, status, message });
    // WebSocket disabled, could add HTTP notification here
  }, []);

  // Add notification
  const addNotification = useCallback((notification) => {
    setNotifications(prev => [notification, ...prev.slice(0, 9)]); // Keep last 10
  }, []);

  // Remove notification
  const removeNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    socket,
    connected,
    activeUsers,
    adminMetrics,
    notifications,
    generationStatus,
    trackActivity,
    trackPageView,
    trackAction,
    notifyGenerationStarted,
    updateGenerationProgress,
    addNotification,
    removeNotification,
    clearNotifications,
    trackEvent
  };
};

export default useWebSocket;